/**
 * Create Workspace Page
 *
 * Super admin page for creating new workspaces under a tenant.
 * Tenant is required — workspaces without a tenant are orphaned and
 * won't appear in any tenant's workspace switcher.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../../stores/prioritization/userStore';
import {
  Building2,
  ArrowLeft,
  Sparkles,
  CheckCircle,
  AlertCircle,
  ChevronDown,
} from 'lucide-react';
import { createWorkspace, createDemoWorkspace, type WorkspacePlan } from '../../lib/workspaceService';
import { listAllTenants, type TenantOverview } from '../../lib/superAdminService';

// ============================================================================
// Types
// ============================================================================

interface FormData {
  tenantId: string;
  name: string;
  slug: string;
  description: string;
  plan: WorkspacePlan;
  ownerEmail: string;
  isDemo: boolean;
  seedData: boolean;
}

// ============================================================================
// Main Component
// ============================================================================

export default function CreateWorkspace() {
  const navigate = useNavigate();
  const user = { id: useUserStore((state) => state.userId) };

  const [tenants, setTenants] = useState<TenantOverview[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);

  const [formData, setFormData] = useState<FormData>({
    tenantId: '',
    name: '',
    slug: '',
    description: '',
    plan: 'starter',
    ownerEmail: '',
    isDemo: false,
    seedData: false,
  });

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    listAllTenants()
      .then(setTenants)
      .catch((e) => console.error('Failed to load tenants:', e))
      .finally(() => setTenantsLoading(false));
  }, []);

  const selectedTenant = tenants.find((t) => t.id === formData.tenantId);

  // Auto-generate workspace slug from name
  const handleNameChange = (name: string) => {
    const slug = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 50);
    setFormData((prev) => ({ ...prev, name, slug }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setError('You must be logged in to create a workspace');
      return;
    }
    if (!formData.tenantId) {
      setError('Please select a tenant for this workspace');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      if (formData.isDemo) {
        await createDemoWorkspace(user.id);
      } else {
        await createWorkspace(
          {
            name: formData.name,
            slug: formData.slug,
            description: formData.description,
            plan: formData.plan,
            isDemo: false,
            tenantId: formData.tenantId,
          },
          user.id
        );
      }

      setSuccess(true);
      setTimeout(() => navigate('/platform-admin/workspaces'), 2000);
    } catch (err) {
      console.error('Error creating workspace:', err);
      setError(err instanceof Error ? err.message : 'Failed to create workspace');
    } finally {
      setCreating(false);
    }
  };

  if (success) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="p-4 bg-[var(--green-dim)] rounded-full w-fit mx-auto mb-6">
            <CheckCircle className="h-12 w-12 text-[var(--green)]" />
          </div>
          <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-2">Workspace Created!</h2>
          <p className="text-[var(--text-ter)] mb-4">
            {formData.isDemo
              ? 'Demo workspace has been created with sample data.'
              : `"${formData.name}" is ready to use under ${selectedTenant?.name ?? 'the selected tenant'}.`}
          </p>
          <p className="text-sm text-[var(--text-ter)]">Redirecting to workspaces list…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <h1 className="text-2xl font-bold text-[var(--text-pri)]">Create Workspace</h1>
        <p className="text-[var(--text-ter)] mt-1">
          Add a new workspace to an existing tenant.
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Tenant Selector */}
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
            Tenant (Organization) *
          </label>
          <div className="relative">
            <select
              value={formData.tenantId}
              onChange={(e) => setFormData((prev) => ({ ...prev, tenantId: e.target.value }))}
              required
              disabled={tenantsLoading}
              className="w-full appearance-none px-4 py-3 pr-10 border border-[var(--rule)] bg-[var(--bg-raised)] focus:ring-2 focus:ring-red-500 focus:border-[var(--coral)] disabled:bg-[var(--surface)] disabled:text-[var(--text-ter)]"
            >
              <option value="">
                {tenantsLoading ? 'Loading tenants…' : '— Select a tenant —'}
              </option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.slug}) — {t.workspaceCount} workspace{t.workspaceCount !== 1 ? 's' : ''}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)] pointer-events-none" />
          </div>
          {selectedTenant && (
            <p className="mt-1 text-xs text-[var(--text-ter)]">
              Plan: <span className="font-medium capitalize">{selectedTenant.plan}</span>
              {' · '}Status: <span className="font-medium capitalize">{selectedTenant.status}</span>
            </p>
          )}
        </div>

        {/* Demo Toggle */}
        <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-4">
          <label className="flex items-start gap-4 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isDemo}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  isDemo: e.target.checked,
                  seedData: e.target.checked,
                  plan: e.target.checked ? 'professional' : prev.plan,
                }))
              }
              className="mt-1 h-5 w-5 text-[var(--mustard)] border-[var(--rule)] rounded focus:ring-amber-500"
            />
            <div>
              <div className="flex items-center gap-2 font-medium text-[var(--mustard)]">
                <Sparkles className="h-5 w-5" />
                Create as Demo Workspace
              </div>
              <p className="text-sm text-[var(--mustard)] mt-1">
                Demo workspaces include sample data and have all professional features enabled.
                Perfect for prospects and training.
              </p>
            </div>
          </label>
        </div>

        {/* Workspace Name */}
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
            Workspace Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Acme — Product Team"
            required
            disabled={formData.isDemo}
            className="w-full px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-red-500 focus:border-[var(--coral)] disabled:bg-[var(--surface)] disabled:text-[var(--text-ter)]"
          />
          {formData.isDemo && (
            <p className="mt-1 text-sm text-[var(--text-ter)]">
              Demo workspaces are auto-named "Demo Workspace"
            </p>
          )}
        </div>

        {/* Slug + URL preview */}
        {!formData.isDemo && (
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
              Workspace Slug *
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value }))}
                placeholder="acme-product"
                required
                pattern="[a-z0-9-]+"
                className="flex-1 px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-red-500 focus:border-[var(--coral)]"
              />
            </div>
            <p className="mt-1.5 text-xs text-[var(--text-ter)] font-mono bg-[var(--surface)] border border-[var(--rule)] rounded px-2 py-1">
              peakpriorities.com/
              <span className="text-[var(--text-ter)]">{selectedTenant?.slug ?? '{tenant}'}</span>
              /
              <span className={formData.slug ? 'text-[var(--text-pri)]' : 'text-[var(--text-ter)]'}>
                {formData.slug || '{workspace}'}
              </span>
            </p>
            <p className="mt-1 text-xs text-[var(--text-ter)]">Lowercase letters, numbers, and hyphens only</p>
          </div>
        )}

        {/* Description */}
        {!formData.isDemo && (
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of this workspace…"
              rows={3}
              className="w-full px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-red-500 focus:border-[var(--coral)] resize-none"
            />
          </div>
        )}

        {/* Plan Selection */}
        {!formData.isDemo && (
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
              Plan
            </label>
            <div className="grid grid-cols-4 gap-3">
              {(['free', 'starter', 'professional', 'enterprise'] as WorkspacePlan[]).map((plan) => (
                <button
                  key={plan}
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, plan }))}
                  className={`p-4 border  text-center transition-all
                    ${formData.plan === plan
                      ? 'border-[var(--coral)] bg-[var(--coral-dim)] ring-2 ring-red-200'
                      : 'border-[var(--rule)] hover:border-[var(--rule)]'}`}
                >
                  <p className="font-medium text-[var(--text-pri)] capitalize">{plan}</p>
                  <p className="text-xs text-[var(--text-ter)] mt-1">
                    {plan === 'free' && '5 items'}
                    {plan === 'starter' && '100 items'}
                    {plan === 'professional' && 'Unlimited'}
                    {plan === 'enterprise' && 'Custom'}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Owner Email */}
        {!formData.isDemo && (
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
              Owner Email
            </label>
            <input
              type="email"
              value={formData.ownerEmail}
              onChange={(e) => setFormData((prev) => ({ ...prev, ownerEmail: e.target.value }))}
              placeholder="admin@example.com"
              className="w-full px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-red-500 focus:border-[var(--coral)]"
            />
            <p className="mt-1 text-sm text-[var(--text-ter)]">
              Leave blank to make yourself the owner
            </p>
          </div>
        )}

        {/* Seed Data */}
        {!formData.isDemo && (
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.seedData}
                onChange={(e) => setFormData((prev) => ({ ...prev, seedData: e.target.checked }))}
                className="h-5 w-5 text-[var(--coral)] border-[var(--rule)] rounded focus:ring-red-500"
              />
              <div>
                <span className="font-medium text-[var(--text-pri)]">Include sample data</span>
                <p className="text-sm text-[var(--text-ter)]">
                  Add example items and processes to get started quickly
                </p>
              </div>
            </label>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="flex items-center gap-2 p-4 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)]">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Submit */}
        <div className="flex items-center gap-4 pt-4">
          <button
            type="submit"
            disabled={creating || !formData.tenantId || (!formData.isDemo && !formData.name)}
            className="flex-1 py-3 bg-[var(--coral)] text-white font-medium hover:bg-[var(--coral)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {creating ? (
              <>
                <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Creating…
              </>
            ) : (
              <>
                <Building2 className="h-5 w-5" />
                {formData.isDemo ? 'Create Demo Workspace' : 'Create Workspace'}
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-3 border border-[var(--rule)] text-[var(--text-sec)] font-medium hover:bg-[var(--surface)] transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
